/**
 * Paste: posts each target's request — the one its review on screen
 * answered — country after country (v2, 26/10/2026: several at once), says
 * what landed (switched off), and hands back to the board, which empties
 * the clipboard and opens the target group. A country that fails stops
 * the run: the ones already pasted leave the window (never pasted twice)
 * and the toast names them.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useState } from 'react';
import type { GroupCopyResult } from '@trafficpulse/shared';

import { getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';

import type { PasteTarget } from './targets/use-paste-targets';
import type { TargetReview } from './targets/target-reviews';
import { useGroupCopyPaste } from './use-group-copy-api';

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

export function pastedLine(results: GroupCopyResult[]): string {
  const containers = results.reduce((n, r) => n + r.containerIds.length, 0);
  const groups = results.reduce((n, r) => n + r.createdListGroups, 0);
  const made = groups ? ` · ${plural(groups, 'list group')} created` : '';
  return `${plural(containers, 'container')}, switched off${made}`;
}

interface Handlers {
  nameOf: (countryKey: string) => string;
  /** Every target pasted. */
  onDone: (pasted: PasteTarget[]) => void;
  /** A later one failed: these were pasted and leave the window. */
  onPartial: (pastedIds: string[]) => void;
}

export function usePasteAll({ nameOf, onDone, onPartial }: Handlers) {
  const paste = useGroupCopyPaste();
  const toast = useToast();
  const [pasting, setPasting] = useState(false);
  const names = (ts: PasteTarget[]) => ts.map((t) => nameOf(t.countryKey)).join(', ');
  const failed = (t: PasteTarget, done: PasteTarget[], err: unknown) => {
    const already = done.length ? ` — already pasted into ${names(done)}` : '';
    toast.error(`Could not paste into ${nameOf(t.countryKey)}`, `${getApiErrorMessage(err)}${already}`);
    if (done.length) onPartial(done.map((d) => d.id));
  };
  const run = async (all: TargetReview[]) => {
    setPasting(true);
    const done: PasteTarget[] = [];
    const results: GroupCopyResult[] = [];
    for (const t of all) {
      if (!t.request) continue;
      try {
        results.push(await paste.mutateAsync(t.request));
        done.push(t.target);
      } catch (err) {
        failed(t.target, done, err);
        setPasting(false);
        return;
      }
    }
    setPasting(false);
    toast.success(`Pasted into ${names(done)}`, pastedLine(results));
    onDone(done);
  };
  return { run, pasting };
}
